import { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, Undo2, Check } from 'lucide-react';
import { useCompanion } from '../utils/companionStore.js';
import { ReceiptCard } from './CompanionCards';
import { undoState, receiptAge } from '../utils/receiptFormat.js';
import { useT } from '../utils/i18n';

// The strip of things that happened. Collapsed: the latest receipt and its
// Undo. Expanded: the last few, newest first. Nothing here is optimistic;
// every row is a server receipt.
export default function ReceiptStrip() {
  const { t } = useT();
  const { receipts, undo, sheet } = useCompanion();
  const [open, setOpen] = useState(false);
  const [dismissedId, setDismissedId] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    const onToast = (e) => { setToast(e.detail); setTimeout(() => setToast(null), 3000); };
    window.addEventListener('cmp-toast', onToast);
    return () => window.removeEventListener('cmp-toast', onToast);
  }, []);

  const latest = receipts[0];
  if (toast) return <div className="toast" style={{ background: 'rgba(239,68,68,0.95)', boxShadow: 'none' }}>{toast}</div>;
  if (!latest || sheet.open) return null;
  if (!open && dismissedId === latest.id) return null;
  const state = undoState(latest);

  const doUndo = async () => {
    try { await undo(latest); }
    catch (err) { window.dispatchEvent(new CustomEvent('cmp-toast', { detail: err?.status === 409 ? t('companion.receipt.changedSince') : (err?.message || t('companion.receipt.undoFailed')) })); }
  };

  return (
    <div className={`cmp-strip ${open ? 'open' : ''}`}>
      {!open && (
        <div className="cmp-strip-row">
          <span className="cmp-receipt-icon">{state === 'undone' ? <Undo2 size={14} /> : <Check size={14} />}</span>
          <div className="cmp-strip-text">
            <span className="cmp-strip-summary">{latest.summary || t('companion.receipt.changed')}</span>
            <span className="cmp-asof"> · {receiptAge(latest.created_at)}{state === 'undone' ? ` · ${t('companion.receipt.undone')}` : ''}</span>
          </div>
          {state === 'undoable' && <button type="button" className="btn btn-secondary cmp-btn-sm" onClick={doUndo}><Undo2 size={14} /> {t('companion.undo')}</button>}
          <button type="button" className="cmp-link" onClick={() => setOpen(true)} aria-label={t('companion.receipt.showAll')}><ChevronUp size={16} /></button>
          <button type="button" className="cmp-link" onClick={() => setDismissedId(latest.id)} aria-label={t('common.close')}>×</button>
        </div>
      )}
      {open && (
        <div className="cmp-strip-list">
          <div className="cmp-strip-head">
            <span>{t('companion.receipt.recent')}</span>
            <button type="button" className="cmp-link" onClick={() => setOpen(false)} aria-label={t('common.close')}><ChevronDown size={16} /></button>
          </div>
          {receipts.slice(0, 8).map(r => <ReceiptCard key={r.id} receipt={r} />)}
        </div>
      )}
    </div>
  );
}
